/**
 * Integrytas Group - Base App (MOBILE)
 * Page: ECM.TEMPLATES — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/EcmTemplateManagerPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail + edit sheet)
 * over the SAME data/logic layer only:
 *   - reads via apiDb (ecm_template + ecm_template_tl), resolving the localized
 *     subject/body against the ACTIVE locale (§8, §17.B4 — never a raw id/code
 *     reaches render; the channel code is shown as a translated label),
 *   - the scalar edits (name / channel / active) route through the Common Routine
 *     via routedUpdate — §2 single write path; never a direct apiDb write.
 * It never re-renders the desktop grid; §16. Localized subject/body editing,
 * bindings and test-send stay on the desktop — the body is shown read-only here
 * (mirrors MobileUserEditSheet's read-only roles).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, Mail, Bell, MessageSquare, Webhook } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobileLeadTone } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ECM.TEMPLATES';

type Channel = 'EMAIL' | 'INAPP' | 'SMS' | 'WEBHOOK';

// §17.B11: CHECK-enum labels via a labelled constant + t(), never bare literals.
const CHANNELS: { value: Channel; labelKey: string; fallback: string }[] = [
  { value: 'EMAIL', labelKey: 'mobile.ecm.channel.EMAIL', fallback: 'Email' },
  { value: 'INAPP', labelKey: 'mobile.ecm.channel.INAPP', fallback: 'In-App' },
  { value: 'SMS', labelKey: 'mobile.ecm.channel.SMS', fallback: 'SMS' },
  { value: 'WEBHOOK', labelKey: 'mobile.ecm.channel.WEBHOOK', fallback: 'Webhook' },
];

// Per-channel lead accent, for a designed (not generic) card list.
const CHANNEL_TONE: Record<Channel, MobileLeadTone> = {
  EMAIL: 'blue', INAPP: 'green', SMS: 'amber', WEBHOOK: 'slate',
};

function ChannelIcon({ channel }: { channel: Channel }) {
  if (channel === 'EMAIL') return <Mail />;
  if (channel === 'INAPP') return <Bell />;
  if (channel === 'SMS') return <MessageSquare />;
  return <Webhook />;
}

interface MobileTemplateRow {
  ecm_template_id: number;
  internal_code: string;
  name: string;
  channel: Channel;
  primary_entity: string | null;
  pk_column: string | null;
  is_active: 'Y' | 'N';
  subject: string | null;
  body_html: string | null;
}

type StatusFacet = 'all' | 'active' | 'inactive';
type ChannelFacet = 'all' | Channel;

interface TlRow { locale_code: string; subject: string | null; body_html: string | null }
type RawTemplateRow = Omit<MobileTemplateRow, 'subject' | 'body_html'> & {
  ecm_template_tl?: TlRow[];
};

export function MobileEcmTemplateManagerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<MobileTemplateRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [channelFacet, setChannelFacet] = useState<ChannelFacet>('all');
  const [selected, setSelected] = useState<MobileTemplateRow | null>(null);
  const [editing, setEditing] = useState<MobileTemplateRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const channelLabel = (c: Channel): string => {
    const def = CHANNELS.find((x) => x.value === c);
    return def ? t(def.labelKey, def.fallback) : c;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('ecm_template')
        .select('ecm_template_id, internal_code, name, channel, primary_entity, pk_column, is_active, ecm_template_tl ( locale_code, subject, body_html )')
        .order('internal_code');
      if (e) throw e;

      const list: MobileTemplateRow[] = ((data ?? []) as RawTemplateRow[]).map((r) => {
        const tls = r.ecm_template_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        const chosen = exact ?? fb;
        return {
          ecm_template_id: r.ecm_template_id,
          internal_code: r.internal_code,
          name: r.name,
          channel: r.channel,
          primary_entity: r.primary_entity,
          pk_column: r.pk_column,
          is_active: r.is_active,
          subject: chosen?.subject ?? null,
          body_html: chosen?.body_html ?? null,
        };
      });
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (subject/body follow it — §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (channelFacet !== 'all' && r.channel !== channelFacet) return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        (r.primary_entity ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet, channelFacet]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.ecm.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const channelFacets: { key: ChannelFacet; label: string }[] = [
    { key: 'all', label: t('mobile.ecm.channel_all', 'All channels') },
    ...CHANNELS.map((c) => ({ key: c.value as ChannelFacet, label: t(c.labelKey, c.fallback) })),
  ];

  const toggleActive = async (r: MobileTemplateRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'ecm_template', 'ecm_template_id', r.ecm_template_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.ecm.title', 'Communication Templates')}
        description={t('mobile.ecm.description', 'Reusable message templates across email, in-app, SMS and webhook channels.')}
      />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.ecm.stat.total', 'Total') },
          { value: rows.filter((r) => r.is_active === 'Y').length, label: t('mobile.ecm.stat.active', 'Active') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.ecm.search', 'Search templates')}
          aria-label={t('mobile.ecm.search', 'Search templates')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={channelFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => channelFacet === v}
        onSelect={(v) => setChannelFacet(v as ChannelFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.ecm.none', 'No templates match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.ecm_template_id}
              lead={<ChannelIcon channel={r.channel} />}
              leadTone={CHANNEL_TONE[r.channel]}
              title={r.name || r.internal_code}
              subtitle={`${r.internal_code} · ${channelLabel(r.channel)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.ecm.detail', 'Template detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ChannelIcon channel={selected.channel} /></span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.ecm.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.ecm.channel_lbl', 'Channel')}
                value={<MobileStatusPill tone="info">{channelLabel(selected.channel)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.ecm.entity', 'Primary entity')} value={selected.primary_entity ?? t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.ecm.pk', 'PK column')} value={selected.pk_column ?? t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.ecm.subject', 'Subject')} value={selected.subject?.trim() ? selected.subject : t('common.none', 'None')} />
            </MobileContentCard>

            {/* Body preview — read-only source; editing stays on the desktop (§16). */}
            <MobileContentCard>
              <div className="m-formrow">
                <label>{t('mobile.ecm.body', 'Body preview')}</label>
                <div className="m-formrow-ro" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '9rem', overflowY: 'auto', fontFamily: 'ui-monospace, monospace' }}>
                  {selected.body_html?.trim()
                    ? selected.body_html
                    : t('mobile.ecm.no_body', 'No body for this locale.')}
                  <span className="m-formrow-hint"> · {t('mobile.ecm.body_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </MobileContentCard>

            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.ecm.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileEcmTemplateEditSheet
        session={session}
        row={editing}
        channelLabel={channelLabel}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the master-table scalar fields (Name + Channel + Active), saved
// through the Common Routine (routedUpdate) — §2. The code is immutable and the
// localized subject/body live in ecm_template_tl (edited on the desktop), so they
// are shown read-only / omitted here (mirrors MobileUserEditSheet).
// ---------------------------------------------------------------------------

interface MobileEcmTemplateEditSheetProps {
  session: AuthSession;
  row: MobileTemplateRow | null;
  channelLabel: (c: Channel) => string;
  onClose: () => void;
  onSaved: () => void;
}

function MobileEcmTemplateEditSheet({ session, row, channelLabel, onClose, onSaved }: MobileEcmTemplateEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [channel, setChannel] = useState<Channel>('EMAIL');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different template is opened.
  useEffect(() => {
    if (!row) return;
    setName(row.name ?? '');
    setChannel(row.channel);
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'ecm_template', 'ecm_template_id', row.ecm_template_id, {
      name: name.trim(),
      channel,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.ecm.edit_sheet', 'Edit template')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ChannelIcon channel={channel} /></span>
              <span>
                <div className="m-nm">{t('mobile.ecm.edit_title', 'Edit template')}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.ecm.code', 'Code')}</label>
                <div className="m-formrow-ro">
                  {row.internal_code}
                  <span className="m-formrow-hint"> · {t('mobile.ecm.code_immutable', 'cannot be changed')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ecm-name">{t('mobile.ecm.name', 'Name')}</label>
                <input
                  id="m-ecm-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ecm-channel">{t('mobile.ecm.channel_lbl', 'Channel')}</label>
                <select id="m-ecm-channel" className="m-sel" value={channel} onChange={(e) => setChannel(e.target.value as Channel)}>
                  {CHANNELS.map((c) => <option key={c.value} value={c.value}>{channelLabel(c.value)}</option>)}
                </select>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.ecm.active_hint', 'Available for sending')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.ecm.content', 'Subject & body')}</label>
                <div className="m-formrow-ro">
                  {row.subject?.trim() ? row.subject : t('mobile.ecm.no_subject', 'No subject for this locale')}
                  <span className="m-formrow-hint"> · {t('mobile.ecm.body_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
